import React from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { useRouter } from 'next/router'

import {
  EmptyStateAction,
  EmptyStateImage,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import { CustomErrorFallback } from 'components/ErrorBoundary'
import {
  CustomError,
  InternalServerError,
  NetworkError,
  ServiceUnavailableError,
} from 'utils/customError'
import Header from 'app/DocumentCollectionPage/components/Header'
import { useSETrackEvent } from 'app/DocumentCollectionPage/hooks/useSETrackEvent'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

interface ICustomErrorBoundaryProps {
  children: React.ReactNode
  resetKeys: string[]
  onReset?: (error: Error) => void
}
const CustomErrorBoundary = ({
  resetKeys,
  children,
  onReset = () => {},
}: ICustomErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      resetKeys={resetKeys}
      FallbackComponent={ErrorFallback}
      onReset={onReset}
    >
      {children}
    </ErrorBoundary>
  )
}

export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const router = useRouter()
  const { SETrackDocumentError } = useSETrackEvent()
  const [, , , getValue] = useLocalStorage('categoryLvl', null)

  const categoryLvl = getValue()

  if (error instanceof CustomError === false) {
    throw error
  }

  const handleTracking = (type: string) => {
    const SOURCE_MAPPING = {
      '/dokumen': 'KUMPULAN_DOKUMEN_LANDING_PAGE',
      '/dokumen/kategori/[id]': categoryLvl,
      '/dokumen/daftar/[id]': 'KUMPULAN_DOKUMEN_DOCUMENT_LIST_PAGE',
      '/dokumen/[id]': 'KUMPULAN_DOKUMEN_DOCUMENT_PAGE',
    }

    SETrackDocumentError(type, {
      event_trigger:
        error instanceof NetworkError
          ? 'ERROR_NO_INTERNET'
          : 'ERROR_SYSTEM_ERROR',
      source: SOURCE_MAPPING[router.route],
    })
  }

  return (
    <div className="relative max-w-xl m-auto mt-14">
      <Header
        showHelpButton
        headerRightOnClick={() => handleTracking('CLICK_HELP_KUMPULAN_DOKUMEN')}
      />

      {error instanceof InternalServerError ||
      error instanceof ServiceUnavailableError ? (
        <EmptyStateWithIllustration
          title="Terjadi kendala sistem"
          description="Silakan coba lagi dalam beberapa saat."
          className="w-full text-center"
        >
          <>
            <EmptyStateImage
              data-testid="error-boundary-image"
              imageSrc="/images/errors/img-error-fixing.png"
            />
            <EmptyStateAction
              data-testid="error-boundary-action-primary"
              size="lg"
              className="w-full mt-2"
              onClick={() => {
                handleTracking('CLICK_TRY_AGAIN')
                resetErrorBoundary('InternalServerError')
              }}
            >
              Coba lagi
            </EmptyStateAction>
          </>
        </EmptyStateWithIllustration>
      ) : (
        <CustomErrorFallback
          error={error}
          resetErrorBoundary={resetErrorBoundary}
          onAction={() => handleTracking('CLICK_TRY_AGAIN')}
        />
      )}
    </div>
  )
}

export default CustomErrorBoundary
